import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {firstValueFrom} from "rxjs";
import {User, UserService} from "../../services/user.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import { getCookie, setCookie, removeCookie} from 'typescript-cookie'
import {MatDialog} from "@angular/material/dialog";
import {ConfirmActionComponent} from "../../dialogs/confirm-action/confirm-action.component";
import {ProfileRequirements, ProfileRequirementsService} from "../../services/profile-requirements.service";
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { EmailSuffixesComponent } from '../../components/email-suffixes/email-suffixes.component';
import { NgIf } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';

import {signIn, signUp, resendSignUpCode, confirmSignUp, signInWithRedirect, getCurrentUser, signOut, autoSignIn} from 'aws-amplify/auth'
import { AlertComponent } from "../../components/alert/alert.component";
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

class FormData {
  constructor(
    public firstName?: string,
    public lastName?: string,
    public email?: string,
    public password?: string,
    public passwordConfirmation?: string
  ) {  }
}


@Component({
    selector: 'app-auth',
    templateUrl: './auth.component.html',
    styleUrls: ['./auth.component.scss'],
    standalone: true,
    imports: [MatTabsModule, NgIf, EmailSuffixesComponent, MatButtonModule, FormsModule, MatFormFieldModule, MatInputModule, RouterLink, AlertComponent, FaIconComponent]
})
export class AuthComponent implements OnInit {


  data: FormData = new FormData();

  error?: string ;

  confirmationCode?: string ;

  signupStatus:  'SIGNING_UP' | "CONFIRMING" | "SIGNING_IN" | "SIGNUP_FAILED" = 'SIGNING_UP' ;

  loading = false ;

  tabIndex?: number ;

  returnUrl?: string ;

  user?: User ;

  requirements?: ProfileRequirements ;

  constructor(
    private $users: UserService,
    private $router: Router,
    private $route: ActivatedRoute,
    private $snackBar: MatSnackBar,
    private $dialog: MatDialog,
    private $requirements: ProfileRequirementsService
  ) { } ;

  async ngOnInit() {

    

    let mode = this.$route.snapshot.queryParams['mode'] ;
    if (mode == 'signup') {
      this.tabIndex = 1 ;
    } else {
      this.tabIndex = 0 ;
    }

    this.returnUrl = this.$route.snapshot.queryParams['returnUrl'] ;

    const unconfirmedEmail = getCookie("UNCONFIRMED_EMAIL");
    console.log(unconfirmedEmail) ;

    if (unconfirmedEmail && unconfirmedEmail != 'undefined') {
      this.data.email = unconfirmedEmail ;
      this.signupStatus = 'CONFIRMING' ;
      this.tabIndex = 1 ;
    } else {
      this.signupStatus = 'SIGNING_UP';
    }

    if (this.returnUrl) {
      this.requirements = await firstValueFrom(this.$requirements.getRequirementsFromReturnUrl(this.returnUrl));
    }

    try {
      this.user = await firstValueFrom(this.$users.getUser());
    } catch (e) {
      console.log('no user');
    }

  }

  get canSignIn(): boolean {

    if (!this.data.email?.length)
      return false ;

    if (!this.data.password?.length)
      return false ;

    return true ;
  }

  get canSignUp() : boolean {

    if (!this.data.email?.length)
      return false ;

    if (!this.data.firstName?.length || !this.data.lastName?.length)
      return false ;

    if (!this.data.password?.length || !this.data.passwordConfirmation?.length)
      return false ;

    if (this.data.password !== this.data.passwordConfirmation)
      return false ;

    return true ;
  }

  async signIn() {

    if (!this.canSignIn){
      return ;
    }

    this.loading = true ;

    try {

      const user = await getCurrentUser() ;
      if (user) {
        await signOut();
      }
    } catch (e) {

    }

    try {
      const authResult = await signIn({
        username: this.data.email!,
        password: this.data.password!
      }) ;

      console.log(authResult);

      await this.finaliseAndRedirect();
    } catch (e:any) {

      console.log(e);
      this.error = e as string;

      this.loading = false ;
    }
  }

  async signUp() {

    if (!this.canSignUp) {
      return ;
    }

    this.loading = true ;
    this.error = undefined ;

    try {

      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: this.$users.getUsername(this.data.email!),
        password: this.data.password!,
        options: {
          userAttributes: {
            email: this.data.email!,
            given_name: this.data.firstName!,
            family_name: this.data.lastName!
          },
          autoSignIn: true
        }
      });

      console.log(`complete: ${isSignUpComplete}, userId: ${userId}, next step: ${nextStep}`);
      setCookie("UNCONFIRMED_EMAIL", this.data.email);

      this.signupStatus = 'CONFIRMING' ;
      this.loading = false ;

    } catch (error) {
      this.error = error as string;
      console.log('error signing up:', error);
      this.loading = false ;
    }
  }

  async resendConfirmationCode() {
    this.loading = true ;

    try {
      const username = this.$users.getUsername(this.data.email!);
      const result = await resendSignUpCode({username});

      console.log("resend result:", result);

      this.$snackBar.open("New confirmation code sent",
        undefined,
        { duration: 3000}
        );

      this.loading = false ;

    } catch (err) {
      console.log('error resending code: ', err);
      this.loading = false ;
    }
  }


  async confirmSignUp() {
    this.error = undefined ;
    this.loading = true ;

    try {
      const confirmResult = await confirmSignUp({
        username: this.$users.getUsername(this.data.email!),
        confirmationCode: this.confirmationCode!
      });

      console.log("confirmation result:", confirmResult);

      removeCookie("UNCONFIRMED_EMAIL");

      this.loading = false ;
      this.signupStatus = "SIGNING_IN";

      const autoSigninResult = await autoSignIn();
    
      if (autoSigninResult.nextStep.signInStep === 'DONE') {
        this.finaliseAndRedirect() ;
      }

    } catch (error) {

      this.error = error as string;
      console.log('error confirming sign up', error);
      this.loading = false ;
    }
  }

  async signInWithGoogle() {
    this.error = undefined ;
    this.loading = true ;

    setCookie("AUTH_RETURN_URL", this.returnUrl);

    try {
      await signInWithRedirect ({ provider: "Google" });
    } catch (error) {
      this.error = error as string;
      console.log('error signing in with google', error);
      this.loading = false ;
    }

  }

  async finaliseAndRedirect() {

    this.loading = true ;

    try {

      let user = await firstValueFrom(this.$users.updateCurrentUser());
      console.log(user);

      this.loading = false ;

      if (this.returnUrl) {
        await this.$router.navigateByUrl(this.returnUrl);
      } else {
        await this.$router.navigateByUrl('/');
      }

    } catch (error) {
      this.error = error as string;
      console.log('error finalizing sign up', error);
      this.loading = false ;
    }
  }

  async abortSignup() {

    const dialogRef = this.$dialog.open(
      ConfirmActionComponent,
      {
        data: {
          markup: '<p>Are you sure you want to abort this signup attempt?</p> <p>You will need to use a different email for any future signup attempts</p>'
        },
        panelClass: 'dialog'
      });

    const result = await firstValueFrom(dialogRef.afterClosed()) ;

    if (result) {
      setCookie("UNCONFIRMED_EMAIL", undefined);
      this.signupStatus = "SIGNING_UP" ;
    }
  }

  handleTabChange() {
    console.log("Changing tab") ;
    //clear the error, since it may not be relevant anymore
    this.error = undefined ;
  }
}
